import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  computed,
  inject,
  input,
  output,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { Discount } from '../../models/discount.models';
import {
  discountStatusLabel,
  formatDiscountAmount,
  formatDiscountLocations,
  formatEligibilityLabel,
  formatTimeOfDay,
  formatTimesApplied,
  resolveDiscountTypeBadge,
} from '../../utils/discount-display.util';
import { WASH_TYPE_LABELS } from '../../constants/discount.constants';
import { DiscountWashType } from '../../models/discount.models';
import {
  SummaryDetailsComponent,
  SummaryDetailsHeader,
  SummaryDetailsRow,
} from '../../../shared/components/summary-details';

/** Read-only Discount Details drawer opened from a Discounts Summary row. */
@Component({
  selector: 'app-discount-details-modal',
  standalone: true,
  imports: [CommonModule, SummaryDetailsComponent],
  templateUrl: './discount-details-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./discount-details-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [DatePipe],
})
export class DiscountDetailsModalComponent {
  private readonly datePipe = inject(DatePipe);

  readonly discount = input<Discount | null>(null);

  readonly close = output<void>();
  readonly edit = output<Discount>();

  readonly statusLabel = discountStatusLabel;
  readonly timesApplied = formatTimesApplied;

  readonly discountId = computed(() => {
    const currentDiscount = this.discount();
    if (!currentDiscount) {
      return '';
    }
    return `#${currentDiscount.code || currentDiscount.id}`;
  });

  readonly statusBadgeClass = computed(() => {
    const currentDiscount = this.discount();
    if (!currentDiscount) {
      return 'status-gray';
    }
    return currentDiscount.is_active !== false ? 'status-green' : 'status-red';
  });

  readonly headerStatusLabel = computed(() => {
    const currentDiscount = this.discount();
    return currentDiscount ? this.statusLabel(currentDiscount) : '';
  });

  readonly summaryHeader = computed((): SummaryDetailsHeader | null => {
    const currentDiscount = this.discount();
    if (!currentDiscount) {
      return null;
    }

    return {
      primary: currentDiscount.name,
      secondary: this.formatAmount(currentDiscount),
      metaLabel: 'DISCOUNT ID',
      metaValue: `#${currentDiscount.code || currentDiscount.id}`,
    };
  });

  readonly summaryRows = computed((): SummaryDetailsRow[] => {
    const currentDiscount = this.discount();
    if (!currentDiscount) {
      return [];
    }

    return [
      {
        fields: [
          { label: 'DISCOUNT TYPE', value: this.formatTypeLabel(currentDiscount) },
          { label: 'STATUS', value: this.statusLabel(currentDiscount) },
        ],
      },
      {
        fields: [
          { label: 'CREATED DATE', value: this.formatDetailDate(currentDiscount.created_at) },
          { label: 'EDITED DATE', value: this.formatDetailDate(currentDiscount.updated_at) },
        ],
      },
      {
        fields: [
          { label: 'TIMES APPLIED', value: this.timesApplied(currentDiscount) },
          { label: 'ELIGIBILITY', value: this.formatEligibility(currentDiscount) },
        ],
      },
      {
        fields: [{ label: 'LOCATION', value: this.formatLocations(currentDiscount) }],
        fullWidth: true,
      },
      {
        fields: [{ label: 'WASH TYPE', value: this.formatWashTypes(currentDiscount) }],
        fullWidth: true,
      },
      {
        fields: [{ label: 'SCHEDULE', value: this.formatScheduleLabel(currentDiscount) }],
        fullWidth: true,
      },
      {
        fields: [{ label: 'RESTRICTION', value: this.formatRestrictionLabel(currentDiscount) }],
        fullWidth: true,
      },
      {
        fields: [
          { label: 'MINIMUM PURCHASE', value: this.formatMinimumPurchaseLabel(currentDiscount) },
        ],
      },
    ];
  });

  readonly renewalRows = computed((): SummaryDetailsRow[] => {
    const schedules = this.discount()?.custom_schedules;
    if (!schedules?.length) {
      return [];
    }

    return schedules.map(schedule => ({
      fields: [
        {
          label: `RENEWAL ${schedule.renewal_number}`,
          value:
            schedule.discount_type === 'percentage'
              ? `${schedule.discount_value}%`
              : `$${schedule.discount_value}`,
        },
      ],
      fullWidth: true,
    }));
  });

  get amount(): string {
    const currentDiscount = this.discount();
    if (!currentDiscount) {
      return '-';
    }
    return this.formatAmount(currentDiscount);
  }

  get typeLabel(): string {
    const currentDiscount = this.discount();
    return currentDiscount ? this.formatTypeLabel(currentDiscount) : '-';
  }

  get locations(): string {
    return this.formatLocations(this.discount());
  }

  get washTypes(): string {
    return this.formatWashTypes(this.discount());
  }

  get scheduleLabel(): string {
    return this.formatScheduleLabel(this.discount());
  }

  get restrictionLabel(): string {
    return this.formatRestrictionLabel(this.discount());
  }

  get minimumPurchaseLabel(): string {
    return this.formatMinimumPurchaseLabel(this.discount());
  }

  private formatAmount(discount: Discount): string {
    return formatDiscountAmount(discount.discount_type, discount.discount_value);
  }

  private formatTypeLabel(discount: Discount): string {
    return resolveDiscountTypeBadge(discount).label;
  }

  private formatEligibility(discount: Discount): string {
    return formatEligibilityLabel(discount.eligibility);
  }

  private formatLocations(discount: Discount | null | undefined): string {
    return formatDiscountLocations(discount?.locations);
  }

  private formatWashTypes(discount: Discount | null | undefined): string {
    const washTypes = discount?.wash_types || [];
    if (washTypes.length === 0) {
      return 'All wash types';
    }
    return washTypes
      .map(washType => WASH_TYPE_LABELS[washType as DiscountWashType] || washType)
      .join(', ');
  }

  private formatScheduleLabel(discount: Discount | null | undefined): string {
    if (!discount || discount.always_active !== false) {
      return 'Always Active';
    }
    return `${this.formatScheduleDate(discount.start_date)} – ${this.formatScheduleDate(
      discount.end_date
    )}`;
  }

  private formatRestrictionLabel(discount: Discount | null | undefined): string {
    const days = discount?.restrict_days_of_week || [];
    const from = formatTimeOfDay(discount?.restrict_from_time);
    const to = formatTimeOfDay(discount?.restrict_to_time);

    const dayLabel =
      days.length === 0
        ? 'Every day'
        : days.map(day => day.charAt(0).toUpperCase() + day.slice(1)).join(', ');

    return from && to ? `${dayLabel}, ${from} – ${to}` : dayLabel;
  }

  private formatMinimumPurchaseLabel(discount: Discount | null | undefined): string {
    const minimum = Number(discount?.minimum_purchase_amount ?? 0);
    return Number.isFinite(minimum) && minimum > 0 ? `$${minimum}` : 'None';
  }

  private formatDetailDate(value: string | null | undefined): string {
    if (!value) {
      return '-';
    }
    return this.datePipe.transform(value, 'MMM d y, h:mma') ?? '-';
  }

  private formatScheduleDate(value: string | null | undefined): string {
    if (!value) {
      return '-';
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString();
  }
}
